/** Gate 1 + Gate 2 quality-check panels. The Final-Synthesis Gate lives in
 *  RichGateFinalBody.tsx; shared primitives in RichGateBits + gateConstants.
 */
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BadgeCheck } from 'lucide-react';
import { asArrayG, asTextG, objG, VERDICT_CLS } from './gateConstants';
import { QualityChecks, BulletList, GateHeaderCard } from './RichGateBits';
import { LegalBasisPanel } from './LegalBasisPanel';
import type { JurisdictionContext } from './LegalBasisPanel';
import { TypeMismatchCallout } from './TypeMismatchCallout';

export function RichGate1Body({ output }: { output: Record<string, unknown> }) {
  const decision = asTextG(output.decision) || 'PROCEED';
  // Deterministic, resolved by the backend before the model ran — see
  // jurisdiction_context.py. Absent on stage rows written before 2026-08-06.
  const jurisdiction = (output.jurisdiction_context ?? null) as JurisdictionContext | null;
  return (
    <div className="space-y-3">
      <GateHeaderCard
        decision={decision}
        rationale={asTextG(output.decision_rationale)}
        confidence={output.gate_confidence}
      />
      <TypeMismatchCallout context={jurisdiction} />
      <LegalBasisPanel context={jurisdiction} />
      <BulletList items={asArrayG(output.critical_issues)} severity="block" label="Critical issues" />
      <BulletList items={asArrayG(output.warnings)} severity="warn" label="Warnings" />
      <QualityChecks checks={objG(output.quality_checks)} />
      {asTextG(output.instructions_for_compliance_agent) && (
        <div className="rounded border bg-muted/30 p-3 text-xs">
          <p className="font-medium text-muted-foreground uppercase tracking-wide mb-1">
            Instructions handed to the Compliance agent
          </p>
          <p>{asTextG(output.instructions_for_compliance_agent)}</p>
        </div>
      )}
    </div>
  );
}

export function RichGate2Body({ output }: { output: Record<string, unknown> }) {
  const decision = asTextG(output.decision) || 'PROCEED';
  const enforce = asTextG(output.contract_enforceability_verdict) || 'UNCERTAIN';
  const enforceCls = VERDICT_CLS[enforce] ?? '';
  const consistency = objG(output.flag_consistency_check);
  const yesNo = (v: unknown): string =>
    v === true ? 'consistent' : v === false ? 'INCONSISTENT' : '—';
  return (
    <div className="space-y-3">
      <GateHeaderCard
        decision={decision}
        rationale={asTextG(output.decision_rationale)}
        confidence={output.gate_confidence}
      />
      <Card className="border-border">
        <CardContent className="pt-3 pb-3 px-4 space-y-1 text-xs">
          <div className="flex items-center gap-2">
            <BadgeCheck className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <span className="font-medium text-sm">Enforceability</span>
            <Badge variant="outline" className={`text-[10px] ml-auto ${enforceCls}`}>{enforce}</Badge>
          </div>
          {asTextG(output.enforceability_rationale) && (
            <p className="text-muted-foreground">{asTextG(output.enforceability_rationale)}</p>
          )}
        </CardContent>
      </Card>
      <Card className="border-border">
        <CardContent className="pt-3 pb-3 px-4 text-xs space-y-0.5">
          <div className="font-medium text-sm mb-1">Flag consistency check</div>
          <div className="grid grid-cols-3 gap-x-3">
            <div><span className="text-muted-foreground">GDPR: </span>{yesNo(consistency.gdpr_consistent)}</div>
            <div><span className="text-muted-foreground">Signature: </span>{yesNo(consistency.signature_consistent)}</div>
            <div><span className="text-muted-foreground">Compliance: </span>{yesNo(consistency.compliance_consistent)}</div>
          </div>
        </CardContent>
      </Card>
      <BulletList items={asArrayG(output.critical_issues)} severity="block" label="Critical issues" />
      <BulletList items={asArrayG(output.warnings)} severity="warn" label="Warnings" />
      <QualityChecks checks={objG(output.quality_checks)} />
      {asTextG(output.instructions_for_gate_agent) && (
        <div className="rounded border bg-muted/30 p-3 text-xs">
          <p className="font-medium text-muted-foreground uppercase tracking-wide mb-1">
            Instructions handed to the Final-Synthesis Gate
          </p>
          <p>{asTextG(output.instructions_for_gate_agent)}</p>
        </div>
      )}
    </div>
  );
}
